import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/// Which modules are on, by key ("shop-pages", "shop-pages.directory"): a part counts as on only while its module is.
export type SiteModulesOn = { [key: string]: boolean };

/// The module keys the app draws by (Databases/Services/Modules, ShopFrontModules on the server).
export const MODULE = {
    shopPages: 'shop-pages',
    follow: 'shop-pages.follow',
    digest: 'shop-pages.digest',
    directory: 'shop-pages.directory',
    homeShelf: 'shop-pages.home-shelf',
    accent: 'shop-pages.accent',
    bangla: 'shop-pages.bangla',
    /// Delivery inside Bangladesh: pickup addresses, Dhaka city and outside fees, the place lists on addresses.
    bdDelivery: 'bd-delivery',
};

const MODULES = makeStateKey<{ On: SiteModulesOn }>('site-modules');

/// The site's modules as shoppers get them (GET api/Modules/State; the owner switches them at /account/modules): which
/// links and boxes to draw. The server render asks once and carries the answer in the page, so the browser starts from
/// it and draws the same links. Until the answer is in, and if it cannot be read, a module counts as off here - the server
/// decides what it serves either way, so this only ever hides a link.
@Injectable({
    providedIn: 'root'
})
export class SiteModulesService {
    private readonly source = new BehaviorSubject<SiteModulesOn>(null);

    /// Every module's state, once known.
    readonly all$: Observable<SiteModulesOn> = this.source.pipe(filter(on => on !== null));

    constructor(private http: HttpClient, private transfer: TransferState, @Inject(PLATFORM_ID) private platformId: any) {
        const carried = transfer.get<{ On: SiteModulesOn }>(MODULES, null);
        if (carried && carried.On) {
            this.source.next(carried.On);
        } else {
            this.load();
        }
    }

    /// Whether a module is on (false until known).
    on$(key: string): Observable<boolean> {
        return this.all$.pipe(map(on => !!on[key]), distinctUntilChanged());
    }

    /// The last known answer, for a template's getter: false until known.
    isOn(key: string): boolean {
        const on = this.source.value;
        return !!(on && on[key]);
    }

    /// What the owner's last press on the Modules page left, so the app's own links follow it at once.
    update(on: SiteModulesOn): void {
        if (on) {
            this.source.next(on);
        }
    }

    private load(): void {
        this.http.get<{ On: SiteModulesOn }>(environment.apiUrl + '/api/Modules/State').pipe(catchError(() => of(null)))
            .subscribe(state => {
                if (state && state.On) {
                    if (isPlatformServer(this.platformId)) {
                        this.transfer.set(MODULES, state);
                    }
                    this.source.next(state.On);
                } else {
                    this.source.next({});
                }
            });
    }
}
